import { useState } from 'react';
import api from '../../../api';

export const useStudentOperations = (tenantId, students, setStudents) => {
  // Form states
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editStudentData, setEditStudentData] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    studentPhoneNumber: '',
    parentPhoneNumber: '',
    academicYearId: '',
    classId: '',
    address: '',
    dateOfBirth: '',
    gender: '',
    parentName: '',
    parentEmail: '',
    admissionDate: new Date().toISOString().split('T')[0],
    rollNumber: '',
    profilePhotoFile: null,
    profilePhotoFileId: null,
    profilePhotoUrl: null
  });
  const [formError, setFormError] = useState('');

  // Edit form states
  const [editClasses, setEditClasses] = useState([]);
  const [editSelectedAcademicYear, setEditSelectedAcademicYear] = useState('');
  const [editSelectedSemester, setEditSelectedSemester] = useState('');
  const [editIsSemesterBased, setEditIsSemesterBased] = useState(false);
  const [editSemesters, setEditSemesters] = useState([]);

  // Bulk operations states
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [showBulkAddModal, setShowBulkAddModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkAddLoading, setBulkAddLoading] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  // View state
  const [viewStudentData, setViewStudentData] = useState(null);

  // Loading states
  const [addLoading, setAddLoading] = useState(false);
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Add new student with profile photo
  const addStudent = async (studentData, profilePhotoFile) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();

    // Append all student fields to formData
    Object.entries(studentData).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        formData.append(key, value.toString());
      }
    });

    // Append profile photo if present
    if (profilePhotoFile) {
      formData.append('profilePhoto', profilePhotoFile);
    }

    try {
      const response = await api.post('/api/students/add', formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (err) {
      throw err.response?.data || { message: 'Error adding student.' };
    }
  };

  // Update student with profile photo
  const handleUpdateStudent = async (payload, profilePhotoFile) => {
    setAddLoading(true);
    setFormError('');

    try {
      const token = localStorage.getItem('token');
      const studentId = editStudentData.studentId || editStudentData._id;

      // If there's a new photo file, upload it first
      if (profilePhotoFile) {
        const photoFormData = new FormData();
        photoFormData.append('profilePhoto', profilePhotoFile);

        await api.post(`/api/files/student-profile/${studentId}`, photoFormData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        });
      }

      // Update student data (without photo)
      const response = await api.put(
        `/api/students/${studentId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      // Update student in local state
      if (setStudents) {
        setStudents(prev => 
          prev.map(s => 
            (s.studentId || s._id) === studentId
              ? { ...s, ...payload }
              : s
          )
        );
      }
      
      return response.data;
    } catch (error) {
      setFormError(
        error.response?.data?.message ||
        error.response?.data?.errorInfo?.message ||
        error.message ||
        'Error updating student'
      );
      throw error;
    } finally {
      setAddLoading(false);
    }
  };

  // ✅ FIXED: Delete student - Now properly sends tenantId in multiple ways
  const handleDeleteStudent = async (studentId, tenantIdArg) => {
    setDeleteLoadingId(studentId);
    try {
      const token = localStorage.getItem('token');
      const finalTenantId = tenantIdArg || tenantId;
      
      if (!finalTenantId) {
        throw new Error('Tenant ID is required for deletion');
      }

      console.log(`🗑️ [Frontend] Deleting student ${studentId} from tenant ${finalTenantId}`);

      // Method 1: Send tenantId in request body (most reliable)
      const response = await api.delete(`/api/students/${studentId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Tenant-ID': finalTenantId // Method 2: Send as header
        },
        data: { tenantId: finalTenantId }, // Method 1: In body
        params: { tenantId: finalTenantId } // Method 3: In query params (as backup)
      });

      console.log('✅ [Frontend] Delete successful:', response.data);

      // Remove student from local state
      if (setStudents) {
        setStudents(prev => prev.filter(s => (s.studentId || s._id) !== studentId));
      }

      return response.data;
    } catch (error) {
      console.error('❌ [Frontend] Delete error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error deleting student';
      throw new Error(errorMessage);
    } finally {
      setDeleteLoadingId(null);
      setConfirmDeleteId(null);
    }
  };

  // Bulk add students
  const handleBulkAddStudents = async (studentsList) => {
    setBulkAddLoading(true);
    try {
      const response = await api.post('/api/students/bulk-add', { 
        students: studentsList,
        tenantId: tenantId // Include tenantId in body
      });
      
      // Add new students to local state
      if (setStudents && response.data.results) {
        const successfulStudents = response.data.results
          .filter(r => r.status === 'success' && r.student)
          .map(r => r.student);
        
        setStudents(prev => [...successfulStudents, ...prev]);
      }
      
      return response.data;
    } catch (err) {
      throw err.response?.data || { message: 'Error adding students in bulk.' };
    } finally {
      setBulkAddLoading(false);
    }
  };

  // ✅ FIXED: Bulk delete students - Now properly sends tenantId
  const handleBulkDelete = async (tenantIdArg) => {
    setBulkDeleteLoading(true);
    try {
      const finalTenantId = tenantIdArg || tenantId;
      
      if (!finalTenantId) {
        throw new Error('Tenant ID is required for bulk delete');
      }

      if (!selectedStudentIds || selectedStudentIds.length === 0) {
        throw new Error('No students selected for deletion');
      }

      console.log(`🗑️ [Frontend] Bulk deleting ${selectedStudentIds.length} students from tenant ${finalTenantId}`);

      const res = await api.post('/api/students/bulk-delete', {
        studentIds: selectedStudentIds,
        tenantId: finalTenantId, // In body
      }, {
        headers: {
          'X-Tenant-ID': finalTenantId // Also as header
        },
        params: { tenantId: finalTenantId } // Also as query param
      });

      // Remove students from local state
      if (setStudents) {
        setStudents(prev => prev.filter(s => !selectedStudentIds.includes(s.studentId || s._id)));
      }
      
      setSelectedStudentIds([]);
      return res.data;
    } catch (err) {
      console.error('❌ [Frontend] Bulk delete error:', err);
      throw err.response?.data || { message: 'Bulk delete failed.' };
    } finally {
      setBulkDeleteLoading(false);
      setShowBulkDeleteModal(false);
    }
  };

  return {
    // Form state
    showAddForm,
    setShowAddForm,
    showEditForm,
    setShowEditForm,
    editStudentData,
    setEditStudentData,
    formData,
    setFormData,
    formError,
    setFormError,
    
    // Operations
    addStudent,
    handleUpdateStudent,
    handleDeleteStudent,
    confirmDeleteId,
    setConfirmDeleteId,
    deleteLoadingId,
    
    // Bulk operations
    selectedStudentIds,
    setSelectedStudentIds,
    showBulkAddModal,
    setShowBulkAddModal,
    showBulkDeleteModal,
    setShowBulkDeleteModal,
    handleBulkAddStudents,
    handleBulkDelete,
    bulkAddLoading,
    bulkDeleteLoading,
    
    // View
    viewStudentData,
    setViewStudentData,
    
    // Loading states
    addLoading,
    setAddLoading,

    // Edit form helpers
    editClasses,
    setEditClasses,
    editSelectedAcademicYear,
    setEditSelectedAcademicYear,
    editSelectedSemester,
    setEditSelectedSemester,
    editIsSemesterBased,
    setEditIsSemesterBased,
    editSemesters,
    setEditSemesters
  };
};